import { useContent, useContentContext } from '@qbe/dfc-content';
import { AddressType, getAddressLabel } from 'components/AddressSearch/addressHelpers';

/**
 * Hook that provides localized field overrides configuration for AddressSearch component.
 * Encapsulates all field labels and error messages from the content management system (currently in resources/cmsCustomer.ts).
 *
 * @param type - The address type (physical, postal, repPostal) used for the lookup field label
 * @returns Field overrides object ready to pass to AddressSearch component
 *
 * @example
 * ```tsx
 * const fieldOverrides = useAddressFieldOverrides('physical');
 * <AddressSearch fieldOverrides={fieldOverrides} ... />
 * ```
 */
export const useAddressFieldOverrides = (type: AddressType) => {
  const { getTextContent } = useContent({ context: useContentContext() });

  // Lookup entry field labels and error messages
  const getIncludeUnitNumberText = getTextContent('addressAutocomplete.addressSearchIncludeUnitNumber');
  const unavailableText = getTextContent('addressAutocomplete.apiUnavailable');
  const noResultsText = getTextContent('addressAutocomplete.noResults');
  const cantFindText = getTextContent('addressAutocomplete.cantFindTheAddress');
  const enterManuallyText = getTextContent('addressAutocomplete.enterItManually');

  // Manual entry field labels and error messages
  const returnToLookupText = getTextContent('addressAutocomplete.returnToLookupText');
  const countryLabel = getTextContent('address.country');
  const streetAddressLabel = getTextContent('address.streetAddress');
  const suburbLabel = getTextContent('address.suburb');
  const stateLabel = getTextContent('address.state');
  const postcodeLabel = getTextContent('address.postcode');
  const invalidFieldError = getTextContent('address.invalidFieldError');
  const invalidPostcodeError = getTextContent('address.invalidPostcodeError');
  const countryRequiredError = getTextContent('address.countryRequiredError');
  const specialCharacterError = getTextContent('address.specialCharacterError');

  // Return the field overrides object to be used in AddressSearch component
  const fieldOverrides = {
    hideRequiredIndicator: true,
    lookup: {
      label: getAddressLabel(type),
      helperText: getIncludeUnitNumberText,
      apiError: unavailableText,
      noResults: noResultsText,
      cantFindText: cantFindText,
      enterManuallyText: enterManuallyText,
      returnToLookupText: returnToLookupText,
    },
    fields: {
      country: {
        label: countryLabel,
        errorMessage: (err: string) => {
          if (err === 'required') return countryRequiredError;
          return undefined;
        },
      },
      streetAddress1: {
        label: streetAddressLabel,
        errorMessage: (err: string) => {
          if (err === 'invalid_characters') return specialCharacterError;
          return invalidFieldError;
        },
        maxLength: 225,
      },
      city: {
        label: suburbLabel,
        errorMessage: (err: string) => {
          if (err === 'invalid_characters') return specialCharacterError;
          return invalidFieldError;
        },
        maxLength: 225,
      },
      state: {
        label: stateLabel,
        errorMessage: (err: string) => {
          if (err === 'invalid_characters') return specialCharacterError;
          return invalidFieldError;
        },
        maxLength: 20,
      },
      postcode: {
        label: postcodeLabel,
        errorMessage: invalidPostcodeError,
        maxLength: 15,
      },
    },
  };

  return fieldOverrides;
};
